import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { Initials } from "@/components/kit";
import { ChecklistCard } from "@/components/Lists";
import { ConfirmDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { teamOf } from "@/lib/selectors";

function ChecklistForm({ initial, person, onSave }) {
  const [f, setF] = useState({ title: initial?.title || "", period: initial?.period || "Manhã", kind: initial?.kind || "operacional", scope: "person", items: (initial?.items || []).map((i) => i.text).join("\n") });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    const texts = [...new Set(f.items.split("\n").map((s) => s.trim()).filter(Boolean))];
    if (!f.title.trim()) return toast.error("Dê um nome ao checklist.");
    if (!texts.length) return toast.error("Inclua pelo menos um item.");
    onSave({ title: f.title.trim(), period: f.period.trim() || "Durante o dia", kind: f.kind, texts, scope: f.scope });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="checklist-form">
      <Field label="Nome do checklist"><Input value={f.title} onChange={set("title")} placeholder="Ex.: Abertura do caixa" data-testid="checklist-title-input" /></Field>
      <Field label="Período"><Input value={f.period} onChange={set("period")} placeholder="Ex.: Manhã, Noite, Semanal" data-testid="checklist-period-input" /></Field>
      <Field label="Tipo de atividade">
        <Select value={f.kind} onValueChange={set("kind")}>
          <SelectTrigger data-testid="checklist-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="operacional">Operacional · exige foto em cada item</SelectItem><SelectItem value="administrativo">Administrativo · sem prova</SelectItem></SelectContent>
        </Select>
      </Field>
      {!initial && (
        <Field label="Aplicar para">
          <Select value={f.scope} onValueChange={set("scope")}>
            <SelectTrigger data-testid="checklist-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="person">Somente {person.name}</SelectItem>
              <SelectItem value="role">Todas com a função {person.role}</SelectItem>
              <SelectItem value="all">Toda a equipe</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      )}
      <Field label="Itens (um por linha)"><Textarea rows={6} value={f.items} onChange={set("items")} data-testid="checklist-items-input" /></Field>
      {initial && <p className="text-xs text-neutral-500">Itens que continuarem com o mesmo texto mantêm a marcação e a prova.</p>}
      <Button type="submit" className="w-full font-semibold" data-testid="checklist-save-button">Salvar checklist</Button>
    </form>
  );
}

const IconBtn = ({ icon: I, label, testId, onClick }) => (
  <button type="button" aria-label={label} data-testid={testId} onClick={onClick} className="rounded p-2 text-neutral-400 transition-colors hover:text-gold-dark"><I className="h-4 w-4" /></button>
);

export function ChecklistManager({ companyId, onReview }) {
  const { state, saveChecklist, deleteChecklist } = useStore();
  const [dlg, setDlg] = useState(null);
  const team = teamOf(state, companyId);
  const close = (v) => !v && setDlg(null);
  const save = ({ title, period, kind, texts, scope }) => {
    const cl = dlg.cl;
    if (cl) {
      const old = Object.fromEntries(cl.items.map((i) => [i.text, i]));
      saveChecklist({ id: cl.id, title, period, kind, items: texts.map((text) => old[text] || { text, done: false }) });
    } else {
      const targets = scope === "all" ? team : scope === "role" ? team.filter((p) => p.role === dlg.person.role) : [dlg.person];
      targets.forEach((p) => saveChecklist({ companyId, collaboratorId: p.id, title, period, kind, items: texts.map((text) => ({ text, done: false })) }));
    }
    toast.success(cl ? "Checklist atualizado." : "Checklist criado.");
    setDlg(null);
  };
  return (
    <div className="space-y-8" data-testid="checklist-manager">
      {team.map((c) => (
        <section key={c.id} data-testid={`owner-checklists-${c.id}`}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3"><Initials name={c.name} /><div><p className="text-sm font-semibold">{c.name}</p><p className="text-xs text-neutral-500">{c.role}</p></div></div>
            <Button size="sm" variant="outline" onClick={() => setDlg({ type: "edit", person: c })} data-testid={`new-checklist-${c.id}`}><Plus className="mr-1 h-4 w-4" /> Novo checklist</Button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {state.checklists.filter((cl) => cl.collaboratorId === c.id).map((cl) => (
              <ChecklistCard
                key={cl.id}
                checklist={cl}
                onReview={onReview}
                actions={<><IconBtn icon={Pencil} label="Editar" testId={`checklist-edit-${cl.id}`} onClick={() => setDlg({ type: "edit", person: c, cl })} /><IconBtn icon={Trash2} label="Excluir" testId={`checklist-delete-${cl.id}`} onClick={() => setDlg({ type: "del", cl })} /></>}
              />
            ))}
          </div>
        </section>
      ))}
      <Dialog open={dlg?.type === "edit"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid="checklist-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{dlg?.cl ? "Editar checklist" : "Novo checklist"}</DialogTitle></DialogHeader>
          {dlg?.type === "edit" && <ChecklistForm initial={dlg.cl} person={dlg.person} onSave={save} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={close} title="Excluir checklist?" description={`${dlg?.cl?.title || ""}. As marcações e provas deste checklist serão apagadas.`} testId="checklist-delete-dialog" onConfirm={() => { deleteChecklist(dlg.cl.id); toast.success("Checklist excluído."); }} />
    </div>
  );
}
